import { auth } from '@/shared/lib';
import { biocareIcon, cardiacIcon } from '@/shared/ui/assets';
import {
  ENV,
  ENV_ENUM,
  ERP_PORTAL_ORIGIN,
  LINK_REDIRECT_AWS_BIOCARE_PORTAL,
  MERCY_PORTAL_URL,
  ROLE,
} from '@/shared/config';
import _ from 'lodash';
import { LANGUAGES, PORTAL_NAMES } from '../config/sidebar';

/**
 * Resolves the per-environment base URLs of the sibling Biotricity portals. Switches on ENV across alpha, delta, staging and the customer default, returning only the ERP home page URL when isHomepage is set and otherwise a map keyed by portal name such as ADMIN, ERP, SALES, BIODIRECT, OPERATION, SUPPORT, SERVICE and QMS.
 *
 * @param isHomepage - When true, return just the ERP home page URL instead of the portal URL map.
 * @returns The ERP home page URL string, or the portal-name to URL map.
 */
const getPortalUrls = (isHomepage = false): any => {
  const erpUrl = `${ERP_PORTAL_ORIGIN}/`;
  switch (ENV) {
    case ENV_ENUM.alpha: {
      return isHomepage
        ? erpUrl
        : {
            ERP: erpUrl,
            SALES: 'https://sales.alpha.bioflux.io/',
            ADMIN: 'https://admin.alpha.bioflux.io/',
            BIODIRECT: 'https://direct.alpha.bioflux.io/',
            OPERATION: 'https://operation.alpha.bioflux.io/',
            SUPPORT: 'https://support2.alpha.bioflux.io/',
            SERVICE: 'https://service.alpha.bioflux.io/',
            QMS: 'https://qms.alpha.bioflux.io/',
          };
    }
    case ENV_ENUM.delta: {
      return isHomepage
        ? erpUrl
        : {
            ADMIN: 'https://admin.delta.bioflux.io/',
            ERP: erpUrl,
            SALES: 'https://sales.delta.bioflux.io/',
            BIODIRECT: 'https://direct.delta.bioflux.io/',
            OPERATION: 'https://operation.delta.bioflux.io/',
            SUPPORT: 'https://support2.delta.bioflux.io/',
            SERVICE: 'https://service.delta.bioflux.io/',
            QMS: 'https://qms.delta.bioflux.io/',
          };
    }
    case ENV_ENUM.staging: {
      return isHomepage
        ? erpUrl
        : {
            ADMIN: 'https://admin.staging.biotricity.com/',
            ERP: erpUrl,
            SALES: 'https://sales.staging.biotricity.com/',
            BIODIRECT: 'https://direct.staging.biotricity.com/',
            OPERATION: 'https://operation.staging.biotricity.com/',
            SUPPORT: 'https://support2.staging.biotricity.com/',
            SERVICE: 'https://service.staging.biotricity.com/',
            QMS: 'https://qms.staging.biotricity.com/',
          };
    }
    default: {
      return isHomepage
        ? erpUrl
        : {
            ADMIN: 'https://admin.customer.biotricity.com/',
            ERP: erpUrl,
            SALES: 'https://sales.customer.biotricity.com/',
            BIODIRECT: 'https://direct.customer.biotricity.com/',
            OPERATION: 'https://operation.customer.biotricity.com/',
            SUPPORT: 'https://support2.customer.biotricity.com/',
            SERVICE: 'https://service.customer.biotricity.com/',
            QMS: 'https://qms.customer.biotricity.com/',
          };
    }
  }
};

/**
 * Builds the catalogue of sibling portals together with the roles allowed to open each one. Pairs the admin, operation, service, QMS, support, sales and BioDirect PORTAL_NAMES entries with their permitted role names and the environment-specific URLs taken from getPortalUrls.
 *
 * @returns The portal entries, each carrying a display name, allowed roles and URL.
 */
const getPortalData = () => {
  const portalUrls = getPortalUrls();
  return [
    {
      name: PORTAL_NAMES.ADMIN,
      roles: ['Admin'],
      url: portalUrls.ADMIN,
    },
    {
      name: PORTAL_NAMES.OPERATION,
      roles: ['Operation Manager', 'Operation Staff'],
      url: portalUrls.OPERATION,
    },
    {
      name: PORTAL_NAMES.SERVICE,
      roles: ['Operation Manager', 'Operation Staff'],
      url: portalUrls.SERVICE,
    },
    {
      name: PORTAL_NAMES.QMS,
      roles: ['QMS Officer'],
      url: portalUrls.QMS,
    },
    {
      name: PORTAL_NAMES.SUPPORT,
      roles: ['Support Staff'],
      url: portalUrls.SUPPORT,
    },
    {
      name: PORTAL_NAMES.SALES,
      roles: ['Sales Admin', 'Sales Manager', 'Sales Representative'],
      url: portalUrls.SALES,
    },
    {
      name: PORTAL_NAMES.BIODIRECT,
      roles: ['Operation Manager', 'Operation Staff'],
      url: portalUrls.BIODIRECT,
    },
  ];
};

/**
 * Resolves the environment-specific URL of a Biocare-family portal from its display name. Nests a switch on portalName across disease management, facility admin, insurance and billing, and the Biocare home page inside a switch on ENV, additionally selecting Mercy-branded hosts for the home page when the loginFrom value in localStorage is mercy.
 *
 * @param portalName - Display name of the target portal.
 * @returns The portal URL, or null when the portal name is unrecognised.
 */
const initialBiocareLink = (portalName: any) => {
  const loginFrom = auth.getLoginFrom();
  const isMercy = _.toLower(loginFrom ?? undefined) === 'mercy';

  switch (portalName) {
    case 'Biocare Disease Management': {
      switch (ENV) {
        case ENV_ENUM.alpha: {
          return 'https://alpha.cardiac.bioflux.io/';
        }
        case ENV_ENUM.delta: {
          return 'https://delta.cardiac.bioflux.io/';
        }
        case ENV_ENUM.staging: {
          return 'https://staging.diseasemgmt.biotricity.com/';
        }
        default: {
          return 'https://diseasemgmt.biotricity.com/';
        }
      }
    }
    case 'Facility Admin Portal': {
      switch (ENV) {
        case ENV_ENUM.alpha: {
          return 'https://organization.alpha.bioflux.io/';
        }
        case ENV_ENUM.delta: {
          return 'https://organization.delta.bioflux.io/';
        }
        case ENV_ENUM.staging: {
          return 'https://organization.staging.biotricity.com/';
        }
        default: {
          return 'https://organization.customer.biotricity.com/';
        }
      }
    }
    case 'Insurance and Billing Portal': {
      switch (ENV) {
        case ENV_ENUM.alpha: {
          return 'https://billing.alpha.bioflux.io/';
        }
        case ENV_ENUM.delta: {
          return 'https://billing.delta.bioflux.io/';
        }
        case ENV_ENUM.staging: {
          return 'https://billing.staging.biotricity.com/';
        }
        default: {
          return 'https://billing.customer.biotricity.com/';
        }
      }
    }
    case 'Biocare Portal Home Page': {
      return isMercy ? MERCY_PORTAL_URL : LINK_REDIRECT_AWS_BIOCARE_PORTAL;
    }
    default: {
      return null;
    }
  }
};

/**
 * Builds the list of Biocare portal shortcuts offered in the sidebar avatar menu. Adds the facility admin entry for FACILITY_ADMIN roles, the disease management entry for non-Mercy logins and the insurance and billing entry for BILLING_USER roles, then always appends the Biocare home page, each entry carrying its icon and the URL from initialBiocareLink.
 *
 * @param roles - The current user's role names, checked for facility admin and billing access.
 * @param isMercy - Whether the session was opened from the Mercy portal, which suppresses the disease management entry.
 * @returns The portal shortcut entries with name, link and icon.
 */
const getBiocareList = (roles: any, isMercy: any) => {
  const biocareList = [];
  if (_.includes(roles, ROLE.FACILITY_ADMIN)) {
    biocareList.push({
      name: 'Facility Admin Portal',
      link: initialBiocareLink('Facility Admin Portal'),
      icon: <img src={cardiacIcon} alt="cardiacIcon" />,
    });
  }

  // dont need check role clinic cuz this portal is only for clinic
  if (!isMercy) {
    biocareList.push({
      name: 'Biocare Disease Management',
      link: initialBiocareLink('Biocare Disease Management'),
      icon: <img src={cardiacIcon} alt="cardiacIcon" />,
    });
  }
  if (_.includes(roles, ROLE.BILLING_USER)) {
    biocareList.push({
      name: 'Insurance and Billing Portal',
      link: initialBiocareLink('Insurance and Billing Portal'),
      icon: <img src={cardiacIcon} alt="cardiacIcon" />,
    });
  }
  biocareList.push({
    name: 'Biocare Portal Home Page',
    link: initialBiocareLink('Biocare Portal Home Page'),
    icon: <img src={biocareIcon} alt="biocareIcon" />,
  });
  return biocareList;
};

/**
 * Resolves a language code to its human-readable name for the sidebar language selector. Looks the code up in the LANGUAGES list and falls back to an empty string when no entry matches.
 *
 * @param selectedLanguage - The language code to resolve.
 * @returns The language display name, or an empty string when unknown.
 */
const getDisplayedLanguageName = (selectedLanguage: any) => {
  /**
   * Computes the LANGUAGES entry whose code equals the selected language code. Derived by scanning the LANGUAGES collection with a code-equality predicate.
   *
   * @returns The matching language object, or undefined when none matches.
   */
  const displayedLanguage = _.find(LANGUAGES, (x) => x.code === selectedLanguage);
  if (displayedLanguage) {
    return displayedLanguage.name;
  }
  return '';
};

export { getBiocareList, getDisplayedLanguageName, getPortalData, getPortalUrls };
